import { useState } from 'react';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import SearchSelect from '@/components/SearchSelect';
import DatePopover from '@/components/DatePopover';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { orgs } from '@/api/catalogs';
import { useDefaults } from '@/store/defaults';
import {
  employees,
  individuals,
  positionsApi,
  departmentsApi,
  workSchedulesApi,
  calcTypesApi,
  EMPLOYMENT_TYPE_LABELS,
  EMPLOYMENT_KIND_LABELS,
  LABOR_CONTRACT_TYPE_LABELS,
  type EmploymentType,
  type EmploymentKind,
  type EmployeeWithDocumentCreate,
} from '@/api/hr';

/**
 * Єдина форма створення працівника (UX-покращення проти 1С-стилю).
 *
 * Один екран: фізособа (нова АБО обрана зі списку) + тип (штат/ЦПУ/без договору)
 * → поля → працівник + наказ про прийом / договір ЦПУ однією атомарною дією.
 * Поля за еталоном 1С: графік роботи, вид розрахунку, вид трудового договору,
 * строк дії, підрозділ/срок оплати ЦПУ. Табельний — автонумерація (порожнє).
 */
export default function EmployeeWithDocumentPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const posList = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list({ limit: 500 }) });
  const schedList = useQuery({ queryKey: ['work-schedules'], queryFn: () => workSchedulesApi.list() });
  const calcList = useQuery({ queryKey: ['calc-types'], queryFn: calcTypesApi.list });
  const indList = useQuery({ queryKey: ['individuals-all'], queryFn: () => individuals.list({ limit: 1000 }) });

  // ── Шапка ──
  // 2026-07-26: старт з організації за замовчуванням з шапки (було «оберіть»).
  const [orgId, setOrgId] = useState(() => useDefaults.getState().defaultOrgId);
  const [type, setType] = useState<EmploymentType>('STAFF');
  const [employmentKind, setEmploymentKind] = useState<EmploymentKind>('MAIN');
  // ч.806: місце діяльності (відокремлений підрозділ); '' = основне місце.
  const [placeId, setPlaceId] = useState('');
  const [code, setCode] = useState('');

  // ── Фізособа: лише вибір зі списку (ручне створення — на сторінці Фізособи) ──
  const [existingIndId, setExistingIndId] = useState('');

  // ── Наказ (STAFF) ──
  const [laborType, setLaborType] = useState('PERMANENT');
  const [hireDate, setHireDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [positionId, setPositionId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [scheduleId, setScheduleId] = useState('');
  const [calcTypeId, setCalcTypeId] = useState('');
  const [salary, setSalary] = useState('');
  const [probationDays, setProbationDays] = useState('');
  const [basis, setBasis] = useState('');  // Підстава (contract_basis наказу)

  // Підрозділи ТIЛЬКИ обраної юрособи — інакше можна було обрати чужий підрозділ,
  // і він не підтягувався у наказ (наказ-редактор фільтрує депи по своїй org).
  const depList = useQuery({
    queryKey: ['departments-by-org', orgId],
    queryFn: () => departmentsApi.list({ organization_id: orgId, limit: 500 }),
    enabled: !!orgId,
  });

  // ── Договір (ЦПУ) ──
  const [subject, setSubject] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amount, setAmount] = useState('');
  const [civilDeptId, setCivilDeptId] = useState('');
  // Вид розрахунку ЦПУ: разово / подобово / погодинно.
  const [civilCalcType, setCivilCalcType] = useState<'ONCE' | 'DAILY' | 'HOURLY'>('ONCE');
  const [paymentTerm, setPaymentTerm] = useState('');

  const mut = useMutation({
    mutationFn: (p: EmployeeWithDocumentCreate) => employees.createWithDocument(p),
    onSuccess: (res) => {
      setError(null);
      // Редірект на створений документ (запит власника 2026-06-15):
      // трудовий → наказ прийому; ЦПУ → договір; інакше (NO_CONTRACT) → картка.
      if (res.document_type === 'HIRE_ORDER' && res.document_id) {
        navigate(`/hire-orders/${res.document_id}`);
      } else if (res.document_type === 'CIVIL_LAW' && res.document_id) {
        navigate(`/civil-law-contracts/${res.document_id}`);
      } else {
        navigate(`/employees/${res.employee_id}`);
      }
    },
    onError: (e) => {
      const err = e as AxiosError<{ detail?: unknown }>;
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Не вдалося створити працівника.');
    },
  });

  // Позначка обовʼязкового поля (запит власника 2026-09-13).
  const Req = () => <span style={{ color: '#dc2626' }} title="обовʼязкове поле"> *</span>;

  function submit(post: boolean) {
    setError(null);
    const payload: EmployeeWithDocumentCreate = {
      organization_id: orgId,
      employment_type: type,
      employment_kind: employmentKind,
      code: code.trim() || null,
      activity_place_id: placeId || null,
      post,
    };
    payload.individual_id = existingIndId;
    if (type === 'STAFF') {
      payload.labor_contract_type = laborType;
      payload.hire_order = {
        // Дата наказу = дата СТВОРЕННЯ (сьогодні), а не дата прийому (hireDate).
        // Час не потрібен — лише дата (рішення власника 2026-06-15).
        document_date: `${new Date().toISOString().slice(0, 10)}T00:00:00Z`,
        hire_date: hireDate,
        position_id: positionId || null,
        department_id: departmentId || null,
        work_schedule_id: scheduleId || null,
        calc_type_id: calcTypeId || null,
        contract_valid_until: validUntil || null,
        salary_amount: Number(salary) || 0,
        probation_days: probationDays ? Number(probationDays) : null,
        contract_basis: basis.trim() || null,
      };
    } else if (type === 'CIVIL_LAW') {
      payload.civil_law = {
        document_date: `${dateFrom}T00:00:00Z`,
        contract_subject: subject.trim() || null,
        date_from: dateFrom,
        date_to: dateTo || null,
        total_amount: Number(amount) || 0,
        calc_type: civilCalcType,
        department_id: civilDeptId || null,
        payment_term: paymentTerm.trim() || null,
      };
    }
    mut.mutate(payload);
  }

  const indValid = !!existingIndId;
  const docValid =
    type === 'NO_CONTRACT' ||
    (type === 'STAFF' && hireDate) ||
    (type === 'CIVIL_LAW' && dateFrom);
  const canSubmit = orgId && indValid && docValid && !mut.isPending;

  // Погодинна: підпис поля «Тариф/оклад» → «Ставка за годину» (alembic 0291).
  const isHourly = (calcList.data ?? []).some(
    (c) => c.id === calcTypeId && c.code === 'HOURLY',
  );

  return (
    <div>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Новий працівник</h2>
        {/* Дії — на нижній статичній плашці за Законом дизайну #6 (запит власника
            2026-09-13): назад / до списку / зберегти (чернетка працівника + наказу) /
            провести (створити і провести наказ). Для «без договору» проведення немає. */}
        <DocumentToolbar
          isNew
          onBackToList={() => navigate('/hr-orders')}
          onSave={() => submit(false)}
          saveDisabled={!canSubmit}
          savePending={mut.isPending}
          saveLabel={type === 'NO_CONTRACT' ? 'створити' : undefined}
          postable={type !== 'NO_CONTRACT'}
          onPost={() => submit(true)}
          postDisabled={!canSubmit}
          postPending={mut.isPending}
          postTitle="Створити працівника і одразу провести наказ про прийом / договір ЦПУ"
          deletable={false}
        />
      </div>

      <form className="card" onSubmit={(e) => e.preventDefault()}>
        {error && <div className="error" style={{ marginBottom: '1rem' }}>{error}</div>}

        {/* Організація + Табельний № + Вид зайнятості — в одну строку */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div className="field" style={{ width: 220, flex: '0 0 auto' }}>
            <label>Організація<Req /></label>
            <select value={orgId}
                    onChange={(e) => {
                      setOrgId(e.target.value);
                      // Скинути обрані підрозділи — вони належали попередній юрособі.
                      setDepartmentId('');
                      setCivilDeptId('');
                      setPlaceId('');
                    }}
                    required>
              <option value="">— оберіть —</option>
              {(orgList.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ width: 160, flex: '0 0 auto' }}>
            <label>Табельний №</label>
            <input value={code} onChange={(e) => setCode(e.target.value)}
                   placeholder="авто, якщо порожнє" />
          </div>
          <div className="field" style={{ flex: '0 0 auto' }}>
            <label>Вид зайнятості<Req /></label>
            <div style={{ display: 'flex', gap: '1.25rem', minHeight: 38, alignItems: 'center' }}>
              {/* «Без договору» (NO_CONTRACT) приховано з вибору — рідкісний
                  інформаційний вид. Код-гілка NO_CONTRACT лишається для сумісності. */}
              {(['STAFF', 'CIVIL_LAW'] as EmploymentType[]).map((t) => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <input type="radio" checked={type === t} style={{ width: 'auto' }} onChange={() => setType(t)} />
                  {EMPLOYMENT_TYPE_LABELS[t]}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* ── Фізособа (пошук) + місце діяльності — в одну строку ── */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start', marginTop: '1.25rem' }}>
          <div className="field" style={{ flex: '1 1 320px', maxWidth: 520 }}>
            <label style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
              <span>Фізособа<Req /></span>
              <a href="/individuals" target="_blank" rel="noreferrer"
                 style={{ fontSize: '0.82rem', fontWeight: 'normal' }}>
                створити ↗
              </a>
            </label>
            {/* Пошуковий вибір (запит власника 2026-09-13): ПІБ + ІПН. */}
            <SearchSelect
              value={existingIndId}
              onChange={setExistingIndId}
              placeholder="— почніть вводити ПІБ або ІПН —"
              items={(indList.data?.items ?? []).map((i) => ({
                id: i.id,
                label: [i.last_name, i.first_name, i.middle_name].filter(Boolean).join(' '),
                hint: i.inn ?? null,
              }))} />
          </div>
          {/* ч.806: місце діяльності — лише юрособи з місцями у картці організації
              («Податкові рахунки»). ПДФО працівника місця — до місцевого бюджету
              місця, сам працівник — у додатковому розрахунку 4ДФ за підрозділ. */}
          {(() => {
            const org = orgList.data?.items.find((o) => o.id === orgId);
            const places = org?.entity_type === 'LEGAL_ENTITY' ? (org.activity_places ?? []) : [];
            if (!places.length) return null;
            return (
              <div className="field" style={{ flex: '0 0 280px' }}>
                <label>Місце діяльності</label>
                <select value={placeId} onChange={(e) => setPlaceId(e.target.value)}
                        title="Відокремлений підрозділ: ПДФО — до місцевого бюджету місця, працівник — у додатковому розрахунку 4ДФ">
                  <option value="">основне місце (організація)</option>
                  {places.map((p) => (
                    <option key={p.id ?? p.name} value={p.id ?? ''}>{p.name}</option>
                  ))}
                </select>
              </div>
            );
          })()}
        </div>

        {/* ── Трудовий договір (STAFF) ── */}
        {type === 'STAFF' && (
          <>
            <h3 style={{ marginTop: '1.25rem' }}>Трудовий договір (наказ про прийом)</h3>
            {/* Вид зайнятості + Вид договору + Дата прийому + Діє по + Випроб. — одна строка. */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field" style={{ minWidth: 200 }}>
                <label>Вид зайнятості</label>
                <select value={employmentKind}
                        onChange={(e) => setEmploymentKind(e.target.value as EmploymentKind)}>
                  {(Object.entries(EMPLOYMENT_KIND_LABELS) as Array<[EmploymentKind, string]>)
                    .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="field" style={{ minWidth: 180 }}>
                <label>Вид договору</label>
                <select value={laborType} onChange={(e) => setLaborType(e.target.value)}>
                  {Object.entries(LABOR_CONTRACT_TYPE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Дата прийому<Req /></label>
                <DatePopover value={hireDate} onChange={(v) => setHireDate(v)} />
              </div>
              {(laborType === 'CONTRACT' || laborType === 'FIXED_TERM') && (
                <div className="field">
                  <label>Діє по</label>
                  <DatePopover value={validUntil} onChange={(v) => setValidUntil(v)} />
                </div>
              )}
              <div className="field">
                <label>Випроб. (днів)</label>
                <input type="number" min={0} value={probationDays} onChange={(e) => setProbationDays(e.target.value)} style={{ width: 110 }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
                  Підрозділ
                  <a href="/departments" target="_blank" rel="noreferrer"
                     style={{ fontSize: '0.82rem', fontWeight: 'normal' }}>створити ↗</a>
                </label>
                <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {(depList.data?.items ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem' }}>
                  Посада
                  <a href="/positions" target="_blank" rel="noreferrer"
                     style={{ fontSize: '0.82rem', fontWeight: 'normal' }}>створити ↗</a>
                </label>
                <select value={positionId} onChange={(e) => setPositionId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {(posList.data?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              {/* Графік роботи — одразу за посадою (запит власника 2026-09-13). */}
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label>Графік роботи</label>
                <select value={scheduleId} onChange={(e) => setScheduleId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {/* Шестиденку приховано з вибору (рішення власника). */}
                  {(schedList.data ?? [])
                    .filter((s) => s.name !== 'Шестиденний робочий тиждень')
                    .map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field" style={{ minWidth: 170 }}>
                <label>Вид розрахунку</label>
                <select value={calcTypeId} onChange={(e) => setCalcTypeId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {/* Відрядна (PIECEWORK) і За середнім (AVERAGE) приховано: рушія
                      їх розрахунку ще немає — формула рахує лише ставка×дні
                      (TECHDEBT TD-PAYROLL-CALCTYPE). Лишаємо Оклад/Погодинну. */}
                  {(calcList.data ?? [])
                    .filter((c) => c.code !== 'PIECEWORK' && c.code !== 'AVERAGE')
                    .map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="field">
                <label>{isHourly ? 'Ставка за годину, грн' : 'Тариф/оклад, грн'}</label>
                <input type="number" step="0.01" value={salary} onChange={(e) => setSalary(e.target.value)} style={{ width: 150 }} />
              </div>
            </div>
            {/* Підстава наказу (contract_basis) — на всю ширину. */}
            <div className="field">
              <label>Підстава</label>
              <input value={basis} onChange={(e) => setBasis(e.target.value)}
                     placeholder="наприклад: Трудовий договір №1 від 15.01.2026" />
            </div>
          </>
        )}

        {/* ── Договір ЦПУ ── */}
        {type === 'CIVIL_LAW' && (
          <>
            <h3 style={{ marginTop: '1.25rem' }}>Договір ЦПУ</h3>
            {/* Предмет договору — на всю ширину. */}
            <div className="field">
              <label>Предмет договору</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            {/* Дата + Підрозділ в одній строці. */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field">
                <label>Діє з<Req /></label>
                <DatePopover value={dateFrom} onChange={(v) => setDateFrom(v)} />
              </div>
              <div className="field">
                <label>Діє до</label>
                <DatePopover value={dateTo} onChange={(v) => setDateTo(v)} />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label>Підрозділ</label>
                <select value={civilDeptId} onChange={(e) => setCivilDeptId(e.target.value)}>
                  <option value="">— не вказано —</option>
                  {(depList.data?.items ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="field">
                <label>Вид розрахунку</label>
                <select value={civilCalcType}
                        onChange={(e) => setCivilCalcType(e.target.value as 'ONCE' | 'DAILY' | 'HOURLY')}>
                  <option value="ONCE">разово</option>
                  <option value="DAILY">подобово</option>
                  <option value="HOURLY">погодинно</option>
                </select>
              </div>
              <div className="field">
                <label>Розмір, грн{civilCalcType === 'DAILY' ? ' (за день)'
                  : civilCalcType === 'HOURLY' ? ' (за годину)' : ''}</label>
                <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: 140 }} />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 200 }}>
                <label>Строк оплати</label>
                <input value={paymentTerm} onChange={(e) => setPaymentTerm(e.target.value)}
                       placeholder="напр. щомісячно / по закінченню / свій варіант" />
              </div>
            </div>
          </>
        )}

        {type === 'NO_CONTRACT' && (
          <p style={{ color: 'var(--muted)', marginTop: '1rem' }}>
            Тип «без договору» — створюється лише картка працівника, без документа.
          </p>
        )}
      </form>
    </div>
  );
}
